import * as _ from 'lodash';
import { IFormFieldModel } from 'app/overview/config/configuration_tab/general/frontend_config/IFormFieldModel';
import { IMapLayerProviderFormModel } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderFormModel';
import {
    FileParameters,
    ImsFeatureLayerProvider,
    ImsFeatureProviderLoadingFromType,
    ImsFeatureProviderType,
} from 'app/config/ImsFeatureLayerProvider';
import { IMapLayerProviderPersister } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderPersister';
import { EventRegistry } from 'app/shared/events/event_registry';
import { THIS_FIELD_IS_REQUIRED } from 'app/overview/config/configuration_tab/general/frontend_config/common/layer_provider_constants';
import { File } from 'app/shared/collections/ims_file';
import { fileHasExtension } from 'app/shared/utilities/file_utils';
import { isValidUrl } from 'app/shared/url_and_history/url_and_history';

interface FeatureLayerProviderFormViewState {
    expanded: boolean;
    isDirty: boolean;
    isValid: boolean;

    // Super mandatory fields
    name: string;
    nameErrMsg: string;

    fileType: ImsFeatureProviderType;
    fileTypeErrMsg: string;
    uuid: string;
    hideInMapLayers: boolean;
    showByDefault: boolean;
    loadingFrom: ImsFeatureProviderLoadingFromType; // file or url

    url: string;

    file: FileParameters;
    fileOrUrlErrMsg: string;

    availableLoadingLocations: any[];
}

export class FeatureLayerProviderFormModel
    implements
        IFormFieldModel<FeatureLayerProviderFormViewState>,
        IMapLayerProviderFormModel<ImsFeatureLayerProvider>
{
    // The form expanded state
    private _expanded: boolean = false;

    private _valueUnderEdit: ImsFeatureLayerProvider;
    private _origValue: ImsFeatureLayerProvider;
    private _persister: IMapLayerProviderPersister;
    private readonly _uuid: string;

    private readonly _availableLoadingLocations;

    private _onChange: EventRegistry;

    constructor(
        persister: IMapLayerProviderPersister,
        origValue: ImsFeatureLayerProvider,
        valueUnderEdit: ImsFeatureLayerProvider,
    ) {
        this._uuid = valueUnderEdit.uuid;

        this._onChange = new EventRegistry();

        if (!this._uuid) {
            throw new Error(`No uuid for feature layer under editing!`);
        }

        this._expanded = false;
        this._persister = persister;
        this._origValue = origValue;
        this._valueUnderEdit = valueUnderEdit;

        // Calculated once at object creation
        this._availableLoadingLocations = [
            ImsFeatureProviderLoadingFromType.FILE,
            ImsFeatureProviderLoadingFromType.URL,
        ].map((type) => {
            return {
                value: type,
                display: type,
            };
        });
    }

    get valueUnderEdit(): ImsFeatureLayerProvider {
        return this._valueUnderEdit;
    }

    get uuid(): string {
        return this._valueUnderEdit.uuid;
    }

    /**
     * Called from parent form
     * Inform that this object that the original value has changed
     *
     * Would be nicer as a listener
     *
     * @param {ImsFeatureLayerProvider} provider
     */
    updateOrigValue(provider: ImsFeatureLayerProvider): void {
        if (!this.isDirty()) {
            // There are no changes
            this._origValue = provider;
            this.revert();
        } else {
            // There are outstanding changes and there are new changes
            // coming in from the API
            this._origValue = provider;
        }
    }

    isDirty(): boolean {
        return this.getViewState().isDirty;
    }

    revert(): void {
        if (!this._origValue) {
            throw new Error('not handled - should never get here');
        }
        this._valueUnderEdit = this._origValue.clone();
        this._notifyListeners();
    }

    delete(): void {
        this._persister.deleteProviderForm(this.uuid);
    }

    getViewState(): FeatureLayerProviderFormViewState {
        const isDirty = this._origValue && this._origValue.isDiff(this._valueUnderEdit);
        const nameErrMsg = _.isEmpty(this._valueUnderEdit.name.trim())
            ? THIS_FIELD_IS_REQUIRED
            : null;
        let url = null;
        let file = null;
        let fileOrUrlErrMsg = null;
        let fileTypeErrMsg = null;
        if (this._valueUnderEdit.loadingFrom == ImsFeatureProviderLoadingFromType.FILE) {
            file = this._valueUnderEdit.file;
            if (file && !this._valueUnderEdit.fileType) {
                this.setFile(file);
            }
            fileOrUrlErrMsg = !file ? 'No file has been uploaded' : null;
            fileTypeErrMsg =
                !fileOrUrlErrMsg && !this._valueUnderEdit.fileType ? 'Invalid type' : null;
        }

        const urlValue = this._valueUnderEdit.url;

        if (this._valueUnderEdit.loadingFrom == ImsFeatureProviderLoadingFromType.URL) {
            if (urlValue && urlValue.trim().length > 0) {
                url = this._valueUnderEdit.url;
            }
            fileOrUrlErrMsg = !url
                ? 'No url has been set'
                : !isValidUrl(url)
                  ? 'Invalid url'
                  : null;
        }

        const isValid = [nameErrMsg, fileOrUrlErrMsg, fileTypeErrMsg].every((s) => s === null);

        return {
            name: this._valueUnderEdit.name,
            uuid: this._valueUnderEdit.uuid,
            fileType: this._valueUnderEdit.fileType,
            loadingFrom: this._valueUnderEdit.loadingFrom,
            url: url,
            file: this._valueUnderEdit.file,
            hideInMapLayers: this._valueUnderEdit.hideInMapLayers,
            showByDefault: this._valueUnderEdit.showByDefault,
            expanded: this._expanded,
            availableLoadingLocations: this._availableLoadingLocations,
            isValid,
            nameErrMsg,
            isDirty,
            fileOrUrlErrMsg,
            fileTypeErrMsg,
        };
    }

    protected _notifyListeners() {
        this._onChange.notifyListeners(this.getViewState());
        // notify upstream form handler that this lower level item has changed
        this._persister.providerFormUpdated();
    }

    destroy(): void {}

    getId(): string {
        return this._uuid;
    }

    isValid(): boolean {
        return this.getViewState().isValid;
    }

    save(): Promise<any> {
        throw new Error('not implemented');
    }

    setExpanded(val: boolean): void {
        this._expanded = val;
        this._persister.providerFormUpdated();
    }

    setName(name: string) {
        this._valueUnderEdit.name = name;
        this._persister.providerFormUpdated();
    }

    setHideInMapLayers(val: boolean) {
        this._valueUnderEdit.hideInMapLayers = !!val;
        this._persister.providerFormUpdated();
    }

    setShowByDefault(val: boolean) {
        this._valueUnderEdit.showByDefault = val;
        this._persister.providerFormUpdated();
    }

    setFileType(fileType: ImsFeatureProviderType) {
        this._valueUnderEdit.fileType = fileType;
        this._persister.providerFormUpdated();
    }

    setLoadingFrom(loadingFrom: ImsFeatureProviderLoadingFromType) {
        this._valueUnderEdit.loadingFrom = loadingFrom;
        if (loadingFrom == ImsFeatureProviderLoadingFromType.URL) {
            this._valueUnderEdit.fileType = this.getFileExtension(this._valueUnderEdit.url);
        }
        this._persister.providerFormUpdated();
    }

    setUrl(url: string) {
        this._valueUnderEdit.url = url.trim().length > 0 ? url : null;
        this._valueUnderEdit.fileType = this.getFileExtension(url);
        this._persister.providerFormUpdated();
    }

    setFile(file: File) {
        if (!file) {
            this._valueUnderEdit.fileType = null;
            this._valueUnderEdit.file = null;
        } else {
            this._valueUnderEdit.file = {};
            this._valueUnderEdit.file['name'] = file.name;
            this._valueUnderEdit.file['fileData'] = file.fileData;
            this._valueUnderEdit.file['type'] = file.type;
            this._valueUnderEdit.file['size'] = file.size;
            this._valueUnderEdit.file['lastModified'] = file.lastModified;

            this._valueUnderEdit.fileType = this.getFileExtension(file.name);
        }

        this._persister.providerFormUpdated();
    }

    getFileExtension(name: string): ImsFeatureProviderType {
        if (!name) return null;
        if (fileHasExtension(name, [ImsFeatureProviderType.KMZ])) {
            return ImsFeatureProviderType.KMZ;
        }
        if (fileHasExtension(name, [ImsFeatureProviderType.KML])) {
            return ImsFeatureProviderType.KML;
        }
        return null;
    }
}
